import React, { useState, useEffect, useCallback } from 'react';
import { Booking, Customer } from '../types';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { Search, UserPlus, Calendar, Filter, Download, Eye, CheckCircle, XCircle, Clock, Plus, CreditCard, Phone, MapPin } from 'lucide-react';

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('active');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Form states
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    booking_type: 'gold' as 'gold' | 'silver' | 'jewellery',
    amount_paid: '',
    gold_rate_per_gram: '',
    silver_rate_per_gram: '',
    weight_grams: '',
    payment_method: 'cash',
    notes: ''
  });

  const fetchBookings = useCallback(async () => {
    try {
      const data = await api.getBookings(selectedStatus);
      setBookings(data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings');
    }
  }, [selectedStatus]);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await api.getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchBookings();
    fetchCustomers();
  }, [fetchBookings, fetchCustomers]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isCustomerDropdownOpen && !target.closest('.customer-dropdown-container')) {
        setIsCustomerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCustomerDropdownOpen]);

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomerData(customer);
    setFormData({
      ...formData,
      customer_id: customer.id.toString(),
      customer_name: customer.name
    });
    setCustomerSearchTerm(customer.name);
    setIsCustomerDropdownOpen(false);
  };

  const clearCustomerSearch = () => {
    setSelectedCustomerData(null);
    setFormData({
      ...formData,
      customer_id: '',
      customer_name: ''
    });
    setCustomerSearchTerm('');
    setIsCustomerDropdownOpen(false);
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    (customer.mobile && customer.mobile.includes(customerSearchTerm))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        customer_id: parseInt(formData.customer_id),
        amount_paid: parseFloat(formData.amount_paid),
        gold_rate_per_gram: formData.gold_rate_per_gram ? parseFloat(formData.gold_rate_per_gram) : null,
        silver_rate_per_gram: formData.silver_rate_per_gram ? parseFloat(formData.silver_rate_per_gram) : null,
        weight_grams: formData.weight_grams ? parseFloat(formData.weight_grams) : null
      };

      const newBooking = await api.createBooking(submitData);
      toast.success('Booking created successfully!');
      setShowForm(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        booking_type: 'gold',
        amount_paid: '',
        gold_rate_per_gram: '',
        silver_rate_per_gram: '',
        weight_grams: '',
        payment_method: 'cash',
        notes: ''
      });
      setSelectedCustomerData(null);
      setCustomerSearchTerm('');
      setIsCustomerDropdownOpen(false);
      fetchBookings();
      
      // Generate and print invoice
      generateBookingInvoice(newBooking);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create booking');
    }

    setLoading(false);
  };

  const generateBookingInvoice = (booking: Booking) => {
    const customer = customers.find(c => c.id === booking.customer_id);
    
    const invoiceHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Booking Receipt - ${booking.booking_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; border-bottom: 2px solid black; padding-bottom: 10px; margin-bottom: 20px; }
          .company-name { font-size: 24px; font-weight: bold; }
          .company-info { font-size: 12px; margin-top: 5px; }
          .invoice-title { font-size: 20px; font-weight: bold; margin: 20px 0; text-align: center; }
          .booking-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .customer-info { margin-bottom: 20px; }
          .booking-details { border: 1px solid black; padding: 15px; margin: 20px 0; }
          .detail-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #ccc; }
          .detail-row:last-child { border-bottom: none; }
          .total-row { font-weight: bold; font-size: 16px; border-top: 2px solid black; margin-top: 10px; padding-top: 10px; }
          .notes { margin: 20px 0; padding: 10px; background: #f9f9f9; border: 1px solid #ddd; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">HARISH JEWELLERS</div>
          <div class="company-info">
            Reg. Address: Raja Market, Sadar Mandi, Chhattarpur, Sahranpur - 247762<br>
            Mob No: 9837408924 Uday Shankar Rastogi (Babbu)<br>
            Mob No: 9012000163 Tushar Rastogi
          </div>
        </div>

        <div class="invoice-title">GOLD/JEWELLERY BOOKING RECEIPT</div>

        <div class="booking-info">
          <div>
            <strong>Booking No:</strong> ${booking.booking_number}<br>
            <strong>Date:</strong> ${new Date(booking.booking_date).toLocaleDateString('en-IN')}
          </div>
          <div>
            <strong>Status:</strong> ${booking.status.toUpperCase()}<br>
            <strong>Payment Method:</strong> ${booking.payment_method.toUpperCase()}
          </div>
        </div>

        <div class="customer-info">
          <h4>Customer Details:</h4>
          <strong>${booking.customer_name}</strong><br>
          ${customer?.mobile ? `Mobile: ${customer.mobile}<br>` : ''}
          ${customer?.address_line1 ? `${customer.address_line1}<br>` : ''}
          ${customer?.city ? `${customer.city}, ` : ''}${customer?.state || ''} ${customer?.pincode || ''}
        </div>

        <div class="booking-details">
          <h4 style="margin-top: 0;">Booking Details</h4>
          <div class="detail-row">
            <span>Booking Type:</span>
            <span style="text-transform: uppercase; font-weight: bold;">${booking.booking_type}</span>
          </div>
          ${booking.gold_rate_per_gram ? `
            <div class="detail-row">
              <span>Gold Rate (per gram):</span>
              <span>${formatCurrency(booking.gold_rate_per_gram)}</span>
            </div>
          ` : ''}
          ${booking.silver_rate_per_gram ? `
            <div class="detail-row">
              <span>Silver Rate (per gram):</span>
              <span>${formatCurrency(booking.silver_rate_per_gram)}</span>
            </div>
          ` : ''}
          ${booking.weight_grams ? `
            <div class="detail-row">
              <span>Weight:</span>
              <span>${booking.weight_grams} grams</span>
            </div>
          ` : ''}
          <div class="detail-row total-row">
            <span>Amount Paid (Advance):</span>
            <span>${formatCurrency(booking.amount_paid)}</span>
          </div>
        </div>

        ${booking.notes ? `
          <div class="notes">
            <strong>Notes:</strong><br>
            ${booking.notes}
          </div>
        ` : ''}

        <div style="margin-top: 30px; padding: 15px; background: #fff3cd; border: 1px solid #ffeaa7;">
          <strong>Important:</strong> This receipt confirms your advance payment of ${formatCurrency(booking.amount_paid)} 
          for ${booking.booking_type} booking at today's rates. 
          ${booking.gold_rate_per_gram ? `Gold rate locked at ${formatCurrency(booking.gold_rate_per_gram)}/gram. ` : ''}
          ${booking.silver_rate_per_gram ? `Silver rate locked at ${formatCurrency(booking.silver_rate_per_gram)}/gram. ` : ''}
          Your advance amount has been added as credit to your account.
        </div>

        <div class="footer">
          <p>Thank you for choosing Harish Jewellers!</p>
          <p>For any queries, please contact us at the above mentioned numbers.</p>
        </div>
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(invoiceHtml);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const updateBookingStatus = async (bookingId: number, status: string) => {
    try {
      await api.updateBookingStatus(bookingId, status);
      toast.success(`Booking status updated to ${status}`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update booking status');
    }
  };

  // Filter bookings based on search and date
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.booking_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = !dateFilter || booking.booking_date.startsWith(dateFilter);
    return matchesSearch && matchesDate;
  });

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-3 py-1 rounded-full text-sm font-medium";
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'completed':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Get booking type styling
  const getBookingTypeBadge = (type: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded text-xs font-medium uppercase";
    switch (type) {
      case 'gold':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'silver':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'jewellery':
        return `${baseClasses} bg-purple-100 text-purple-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Calendar className="h-8 w-8 mr-3 text-indigo-600" />
                Jewellery Bookings
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage customer bookings and advance payments
              </p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              <Plus className="h-5 w-5 mr-2" />
              {showForm ? 'Cancel' : 'New Booking'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg border p-8 mb-8">
            <div className="flex items-center mb-6">
              <Plus className="h-6 w-6 mr-3 text-indigo-600" />
              <h2 className="text-2xl font-bold text-gray-900">Create New Booking</h2>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Selection */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer *
                  </label>
                  <div className="relative customer-dropdown-container">
                    {!selectedCustomerData ? (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10 pointer-events-none" />
                        <input
                          type="text"
                          className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                          placeholder="Search customer by name or mobile..."
                          value={customerSearchTerm}
                          onChange={(e) => {
                            setCustomerSearchTerm(e.target.value);
                            setIsCustomerDropdownOpen(true);
                          }}
                          onFocus={() => setIsCustomerDropdownOpen(true)}
                          required
                        />
                        {isCustomerDropdownOpen && (
                          <div className="absolute z-50 w-full mt-1 bg-white shadow-xl max-h-60 rounded-lg py-1 text-base overflow-auto border border-gray-200">
                            {filteredCustomers.length > 0 ? (
                              filteredCustomers.map((customer) => (
                                <div
                                  key={customer.id}
                                  className="cursor-pointer hover:bg-indigo-50 py-3 px-4 transition-colors duration-150"
                                  onClick={() => handleCustomerSelect(customer)}
                                >
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <span className="font-medium text-gray-900">{customer.name}</span>
                                      {customer.mobile && (
                                        <div className="flex items-center mt-1">
                                          <Phone className="h-3 w-3 mr-1 text-gray-400" />
                                          <span className="text-sm text-gray-500">{customer.mobile}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {customer.address_line1 && (
                                    <div className="flex items-center mt-1">
                                      <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                                      <div className="text-xs text-gray-500 truncate">
                                        {customer.address_line1}
                                        {customer.city && `, ${customer.city}`}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))
                            ) : customerSearchTerm ? (
                              <div className="py-4 px-3 text-center text-gray-500">
                                No customers found
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                            <span className="text-indigo-600 font-semibold">
                              {selectedCustomerData.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{selectedCustomerData.name}</div>
                            {selectedCustomerData.mobile && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Phone className="h-3 w-3 mr-1" />
                                {selectedCustomerData.mobile}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 transition-colors duration-150"
                          onClick={clearCustomerSearch}
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Booking Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Booking Type *
                  </label>
                  <select
                    value={formData.booking_type}
                    onChange={(e) => setFormData({...formData, booking_type: e.target.value as 'gold' | 'silver' | 'jewellery'})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="jewellery">Jewellery</option>
                  </select>
                </div>

                {/* Amount Paid */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount Paid (Advance) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount_paid}
                    onChange={(e) => setFormData({...formData, amount_paid: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder="₹ 0.00"
                    required
                  />
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>

                {/* Gold Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gold Rate (per gram)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.gold_rate_per_gram}
                    onChange={(e) => setFormData({...formData, gold_rate_per_gram: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder="Current gold rate"
                  />
                </div>

                {/* Silver Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Silver Rate (per gram)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.silver_rate_per_gram}
                    onChange={(e) => setFormData({...formData, silver_rate_per_gram: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder="Current silver rate"
                  />
                </div>

                {/* Weight */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weight (grams)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    value={formData.weight_grams}
                    onChange={(e) => setFormData({...formData, weight_grams: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder="Weight in grams"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    rows={3}
                    placeholder="Additional notes about the booking..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Create Booking & Print Receipt
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
              
              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Status:</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="all">All</option>
                </select>
              </div>

              {/* Date Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600">Date:</label>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm w-64"
              />
            </div>
          </div>
        </div>

        {/* Bookings Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredBookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow duration-200">
              {/* Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">
                        {booking.customer_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{booking.customer_name}</h3>
                      <p className="text-sm text-gray-500">{booking.booking_number}</p>
                    </div>
                  </div>
                  <span className={getStatusBadge(booking.status)}>
                    {booking.status === 'active' && <Clock className="h-4 w-4 mr-1" />}
                    {booking.status === 'completed' && <CheckCircle className="h-4 w-4 mr-1" />}
                    {booking.status === 'cancelled' && <XCircle className="h-4 w-4 mr-1" />}
                    {booking.status}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={getBookingTypeBadge(booking.booking_type)}>
                    {booking.booking_type}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(booking.booking_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Amount Paid:</span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(booking.amount_paid)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payment Method:</span>
                    <span className="text-sm font-medium capitalize flex items-center">
                      <CreditCard className="h-4 w-4 mr-1" />
                      {booking.payment_method}
                    </span>
                  </div>

                  {booking.gold_rate_per_gram && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Gold Rate:</span>
                      <span className="text-sm font-medium">{formatCurrency(booking.gold_rate_per_gram)}/g</span>
                    </div>
                  )}

                  {booking.silver_rate_per_gram && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Silver Rate:</span>
                      <span className="text-sm font-medium">{formatCurrency(booking.silver_rate_per_gram)}/g</span>
                    </div>
                  )}

                  {booking.weight_grams && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Weight:</span>
                      <span className="text-sm font-medium">{booking.weight_grams} grams</span>
                    </div>
                  )}

                  {/* Notes Section */}
                  {(
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="flex items-start space-x-2">
                        <svg className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="flex-1">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Notes:</span>
                          <p className="text-sm text-gray-800 mt-1 leading-relaxed">{booking.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => generateBookingInvoice(booking)}
                    className="inline-flex items-center px-3 py-2 border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Print
                  </button>
                  
                  {booking.status === 'active' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updateBookingStatus(booking.id, 'completed')}
                        className="inline-flex items-center px-3 py-2 border border-green-200 rounded-lg text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Complete
                      </button>
                      <button
                        onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                        className="inline-flex items-center px-3 py-2 border border-red-200 rounded-lg text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredBookings.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || dateFilter ? 
                'No bookings match your current filters.' : 
                'No bookings found for the selected status.'
              }
            </p>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Booking
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings; 